import { useState } from "react"
import { TrendingUp, Trash2, RefreshCw } from "lucide-react"
import { useGetPopularSearchesQuery, useDeletePopularSearchMutation, useClearPopularSearchesMutation } from "../features/admin/adminApi"

export default function SearchInsightsPage() {
  const { data: searches = [], isLoading, refetch } = useGetPopularSearchesQuery()
  const [deleteSearch] = useDeletePopularSearchMutation()
  const [clearAll, { isLoading: clearing }] = useClearPopularSearchesMutation()
  const [confirming, setConfirming] = useState(false)

  const maxCount = searches[0]?.count ?? 1

  const handleDelete = async (id: number, query: string) => {
    if (!window.confirm(`Remove "${query}" from popular searches?`)) return
    await deleteSearch(id)
  }

  const handleClear = async () => {
    await clearAll()
    setConfirming(false)
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Search Insights</h1>
          <p className="text-sm text-gray-500 mt-1">Most searched queries on the platform</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg" title="Refresh">
            <RefreshCw size={16} />
          </button>
          {!confirming ? (
            <button onClick={() => setConfirming(true)}
              className="px-3 py-2 text-sm text-red-600 font-medium border border-red-200 rounded-lg hover:bg-red-50">
              Clear All
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Are you sure?</span>
              <button onClick={handleClear} disabled={clearing}
                className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                {clearing ? "Clearing…" : "Yes, clear"}
              </button>
              <button onClick={() => setConfirming(false)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
          <TrendingUp size={16} className="text-blue-500" />
          <span className="font-semibold text-gray-900">Popular Searches</span>
          <span className="ml-auto text-xs text-gray-400">{searches.length} queries</span>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-sm text-gray-400">Loading…</div>
        ) : searches.length === 0 ? (
          <div className="p-12 text-center">
            <TrendingUp size={36} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-400 text-sm">No search data yet.</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {searches.map((s, i) => {
              const barWidth = Math.round((s.count / maxCount) * 100)
              return (
                <li key={s.id} className="px-5 py-3 hover:bg-gray-50">
                  <div className="flex items-center gap-4">
                    {/* Rank */}
                    <span className="text-xs font-bold text-gray-400 w-5 text-right flex-shrink-0">{i + 1}</span>

                    {/* Query + bar */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-medium text-gray-900 truncate">{s.query}</p>
                        <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                          <span className="text-xs font-semibold text-blue-600">{s.count.toLocaleString()}</span>
                          <span className="text-xs text-gray-400">
                            {new Date(s.lastSearchedAt).toLocaleDateString()}
                          </span>
                          <button onClick={() => handleDelete(s.id, s.query)}
                            className="p-1 text-gray-300 hover:text-red-500 transition-colors">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-400 rounded-full" style={{ width: `${barWidth}%` }} />
                      </div>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
